/**
 * Better Auth Service Implementation
 * Following Better Auth best practices and standards
 * Reference: https://www.better-auth.com/docs/basic-usage
 */

import { authClient, getSession } from '../lib/auth-client'

export interface User {
  id: string
  email: string
  name?: string
  display_name?: string  // Legacy compatibility
  image?: string | null
  emailVerified: boolean
  createdAt: string
  created_at?: string  // Legacy compatibility
  updatedAt: string
  last_login?: string  // Legacy compatibility
}

export interface AuthResponse {
  authenticated: boolean
  user?: User
  message?: string
}

export interface SignInData {
  email: string
  password: string
}

export interface SignUpData {
  email: string
  password: string
  name?: string
  display_name?: string
  captchaToken?: string
}

// Legacy type aliases for compatibility
export type LoginRequest = SignInData
export type RegisterRequest = SignUpData

/**
 * Better Auth Service - Following Better Auth best practices
 * All session management is handled by Better Auth via secure HTTP-only cookies
 */
export class AuthService {
  private currentUser: User | null = null

  constructor() {
    // Better Auth handles all session state via cookies
    // No manual state management needed
  }

  /**
   * Check authentication using Better Auth client
   * This is the standard way to check auth status
   */
  async checkAuth(): Promise<AuthResponse> {
    try {
      console.log('🔧 Checking authentication with Better Auth...')
      
      const { data: session, error } = await getSession()
      
      if (error) {
        console.warn('⚠️ Session check error:', error.message)
        return { authenticated: false }
      }
      
      if (session && session.user) {
        console.log('✅ User authenticated:', session.user.email)
        
        // Convert Better Auth user to our interface
        const user: User = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.name,
          display_name: session.user.name, // Legacy compatibility
          image: session.user.image,
          emailVerified: session.user.emailVerified,
          createdAt: session.user.createdAt?.toISOString() || new Date().toISOString(),
          created_at: session.user.createdAt?.toISOString() || new Date().toISOString(),
          updatedAt: session.user.updatedAt?.toISOString() || new Date().toISOString()
        }
        
        this.currentUser = user
        return {
          authenticated: true,
          user
        }
      }
      
      this.currentUser = null
      return { authenticated: false }
    } catch (error) {
      console.error('❌ Auth check error:', error)
      this.currentUser = null
      return { authenticated: false }
    }
  }

  /**
   * Sign in with email and password using Better Auth
   */
  async signIn(data: SignInData): Promise<AuthResponse> {
    try {
      console.log('🔧 Signing in with Better Auth...')
      
      // Determine the correct callback URL based on environment
      const isDev = import.meta.env.MODE === 'development';
      const baseUrl = isDev 
        ? 'http://localhost:5174' 
        : window.location.origin; // Use current origin (works with any domain automatically)
      
      const { data: signInResponse, error } = await authClient.signIn.email({
        email: data.email,
        password: data.password,
        callbackURL: `${baseUrl}/dashboard`
      })
      
      if (error) {
        throw new Error(error.message || 'Failed to sign in')
      }
      
      console.log('✅ Sign in successful:', signInResponse.user?.email)
      
      // Check if user is verified after successful sign-in
      if (signInResponse.user && !signInResponse.user.emailVerified) {
        // Sign out the user since they're not verified
        await this.signOut()
        throw new Error("Please verify your email address before signing in. Check your inbox for a verification email.")
      }
      
      // Return fresh auth status
      return await this.checkAuth()
    } catch (error) {
      console.error('❌ Sign in error:', error)
      throw error
    }
  }

  /**
   * Sign up with email and password using Better Auth
   */
  async signUp(data: SignUpData): Promise<AuthResponse> {
    try {
      console.log('🔧 Signing up with Better Auth...')
      
      // Determine the correct callback URL based on environment
      const isDev = import.meta.env.MODE === 'development';
      const baseUrl = isDev 
        ? 'http://localhost:5174' 
        : window.location.origin; // Use current origin (works with any domain automatically)
      
      const { data: signUpResponse, error } = await authClient.signUp.email({
        email: data.email,
        password: data.password,
        name: data.name || data.display_name || '',
        callbackURL: `${baseUrl}/dashboard`
      })
      
      if (error) {
        throw new Error(error.message || 'Failed to sign up')
      }
      
      console.log('✅ Sign up successful:', signUpResponse.user?.email)
      
      // Return fresh auth status
      return await this.checkAuth()
    } catch (error) {
      console.error('❌ Sign up error:', error)
      throw error
    }
  }

  /**
   * Sign out using Better Auth
   */
  async signOut(): Promise<void> {
    try {
      console.log('🔧 Signing out with Better Auth...')
      
      await authClient.signOut({
        fetchOptions: {
          onSuccess: () => {
            console.log('✅ Sign out successful')
            this.currentUser = null
          }
        }
      })
    } catch (error) {
      console.error('❌ Sign out error:', error)
      // Even if sign out fails, clear local state
      this.currentUser = null
    }
  }

  /**
   * Get current user (cached)
   */
  getCurrentUser(): User | null {
    return this.currentUser
  }

  /**
   * Clear cached authentication state
   * Better Auth handles session state via cookies, so this just clears local cache
   */
  clearAuthCache(): void {
    this.currentUser = null
  }

  /**
   * Force logout - clears local state and redirects
   * Used for emergency logout scenarios
   */
  forceLogout(): void {
    console.log('🔴 Force logout - clearing local state')
    this.currentUser = null
    // Better Auth handles the actual session clearing via cookies
  }

  /**
   * Legacy method for compatibility
   */
  async logout(): Promise<void> {
    return this.signOut()
  }

  /**
   * Legacy method for compatibility
   */
  async login(data: SignInData): Promise<AuthResponse> {
    return this.signIn(data)
  }

  /**
   * Legacy method for compatibility
   */
  async register(data: SignUpData): Promise<AuthResponse> {
    return this.signUp(data)
  }

  /**
   * Resend email verification
   * Note: Better Auth handles email verification automatically during signup.
   * This method provides a fallback for manual resend requests.
   */
  async resendVerification(email: string): Promise<void> {
    try {
      console.log('🔧 Resending verification email...')
      
      // Better Auth standard approach: Use the auth service's resend endpoint
      // This follows Better Auth's pattern of using the auth service directly
      // Get the auth service URL from environment
      const authBaseUrl = import.meta.env.VITE_AUTH_BASE_URL || 'http://localhost:3001'
      const cleanAuthUrl = authBaseUrl
        .replace(/\/+$/, '') // Remove trailing slashes
        .replace(/\/auth$/, '') // Remove /auth suffix if present
        .replace(/\/api\/auth$/, '') // Remove /api/auth suffix if present
      
      const response = await fetch(`${cleanAuthUrl}/api/auth/resend-verification`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      })
      
      if (!response.ok) {
        throw new Error(`Failed to resend verification: ${response.statusText}`)
      }
      
      console.log('✅ Verification email resent successfully')
    } catch (error) {
      console.error('❌ Resend verification error:', error)
      throw error
    }
  }

  /**
   * Change user password
   * Note: Better Auth recommends using "forgot password" flow for security.
   * This method provides direct password change for authenticated users.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      console.log('🔧 Changing password...')
      
      // Better Auth standard approach: Use the auth service's change-password endpoint
      // This follows Better Auth's pattern of using the auth service directly
      // Get the auth service URL from environment
      const authBaseUrl = import.meta.env.VITE_AUTH_BASE_URL || 'http://localhost:3001'
      const cleanAuthUrl = authBaseUrl
        .replace(/\/+$/, '') // Remove trailing slashes
        .replace(/\/auth$/, '') // Remove /auth suffix if present
        .replace(/\/api\/auth$/, '') // Remove /api/auth suffix if present
      
      const response = await fetch(`${cleanAuthUrl}/api/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to change password')
      }
      
      console.log('✅ Password changed successfully')
    } catch (error) {
      console.error('❌ Change password error:', error)
      throw error
    }
  }
}

// Create singleton instance
export const authService = new AuthService()

// Export the checkAuth function for backward compatibility
export const checkAuth = () => authService.checkAuth()